export interface Audit {
    dtInsert: string;
    nInsertUser: number;
    dtUpdate: string;
    nUpdateUser: number;
    dtDelete: string;
    nDeleteUser: number;
}

export interface Settings extends Audit {
    nSettID: number;
    sSettType: string;
    nDivID: number;

    // CT related (not settings)
    sAppTheme: string;
    sCtVersion: string;

    // Settings
    aAllowedDestinationCountries: any[];
    aAllowedOriginCountries: any[];
    aIncoTerms: IncoTerm[];
    aCustPortInsurances: any[];
    aOrderTypes: OrderCreationFlow[];
    aSalesOrganizationIDs: number[];
    aSvcLevelIDs: number[];
    aSvcLevels: ServiceLevel[];
    aUsefulLinks: string[];
    bAccountRegistration: boolean;
    bAllowAllExternalMODocuments: boolean;
    bAllowCostObject: boolean;
    bAllowCreateOrdersAsap: boolean;
    bAllowInsertLocation: boolean;
    bAllowOrderDocumentUpload: boolean;
    bAllowOverrideGrossWeight: boolean;
    bAllowOverrideHangingGarmentCount: boolean;
    bAllowOverrideLmeter: boolean;
    bAllowOverridePalletCount: boolean;
    bAllowPartDocumentUpload: boolean;
    bCustomerCreditStop: boolean;
    bCustomerLinkedParts: boolean;
    bDefaultDivEnvironment: boolean;
    bHandlingGuidelines: boolean;
    bHazardous: boolean;
    bHideDeliveryAddress: boolean;
    bMapEnabled: boolean;
    bMapShowCourierLastLocation: boolean;
    bMapShowGPSTrack: boolean;
    bOrderCreation: boolean;
    bOrderInformationDocument: boolean;
    bOutOfficeHoursAsap: boolean;
    bOutOfficeHoursCreateOrder: boolean;
    bPartAssessment: boolean;
    bRequirePartStackabilityOverrides: boolean;
    bRequireMaterialOrderReference: boolean;
    bSearch: boolean;
    bShowCarrierName: boolean;
    bShowCarrierStatus: boolean;
    bShowCheckpoints: boolean;
    bShowContactUsDashboard: boolean;
    bShowContactUsHomepage: boolean;
    bShowDashboard: boolean;
    bShowDriverName: boolean;
    bShowETA: boolean;
    bShowEarlyCollDeli: boolean;
    bShowLateCollDeli: boolean;
    bShowMobileDriver: boolean;
    bShowPartsAndPackagingTable: boolean;
    bShowRecentOrdersDashboard: boolean;
    bShowScheduledTimeRevision: boolean;
    bShowServiceHoursDashboard: boolean;
    bShowServiceHoursHomepage: boolean;
    bShowTrailerLicensePlate: boolean;
    bShowTruckLicensePlate: boolean;
    bShowUsefulLinksDashboard: boolean;
    bTemperate: boolean;
    bTransloadingFacilities: boolean;
    fMaxDistance: number;
    fMaxGrossWeight: number;
    fMaxNetWeight: number;
    fMaxVolume: number;
    jServiceHours: OfficeHours;
    jStyles: CupStyle;
    jTransloadingFacilitiesConditions: TransloadingFacilitySetting;
    nBackgroundImageFsFileID: number;
    nDefaultDeliveryLocaID: number;
    nEarliestFromTo: number;
    nGroupID: number;
    nLogoHorizontalFsFileID: number;
    nLogoSquareFsFileID: number;
    /**
     * Allow the CUP user to see documents with extra AOR Types than the default one ( External
     * visible by customer )
     */
    sAllowSelectDocumentsForExtraAorTypes?: SAllowSelectDocumentsForExtraAorTypes;
    /**
     * Allow the CUP user to upload documents with extra AOR Types than the default one (
     * External visible by customer )
     */
    sAllowUploadDocumentsForExtraAorTypes?: SAllowUploadDocumentsForExtraAorTypes;
    sContactUsMsg: string;
    sEmailAddress: string;
    sEnvironmentCode: string;
    sFooterText: string;
    sHomePageTagLine: string;
    sInOfficeHoursText: string;
    sOrderFlow: OrderFlow;
    sOrderInformationDocumentFooter: string;
    sOrderLogic: string;
    sOutOfficeHoursText: string;
    sPhoneNumber: string;
    sPrivacyPolicyLink: string;
    sQuotationTermsLink: string;
    sRegistrationType: string;
    sTermsLink: string;
    sRequireNetWeight: FormFieldOptions;
    sRequireGoodsValue: FormFieldOptions;
    sOverrideTotalNetWeight: FormFieldOptions;
    sOverrideTotalGoodsValue: FormFieldOptions;
    sWelcomeMsg: string;
    sRequireIncoTermCode: FormFieldOptions;
    sRequireIncoTermLocation: FormFieldOptions;
    sRequireRecommendedArrivePort: FormFieldOptions;
    sRequireRecommendedDepartPort: FormFieldOptions;
    sRequireShipperLoca: FormFieldOptions;
    sRequireConsigneeLoca: FormFieldOptions;
    sRequireNotifyLoca: FormFieldOptions;
}

export enum OrderFlow {
    NORMAL_FLOW = 'NORMAL_FLOW',
    COLLECTION_ONLY = 'COLLECTION_ONLY',
}

export enum OrderCreationFlow {
    ONE_TO_ONE = 'ONE_TO_ONE',
    MANY_TO_ONE = 'MANY_TO_ONE',
    ONE_TO_MANY = 'ONE_TO_MANY'
}

export interface ServiceLevel {
    nSvcLevelID: number;
    sSvcLevelCode: string;
    sSvcLevelFull: string;
    sSvcLevelName: string;
}


export interface OfficeHours {
    aAvailability: OfficeHour[];
    sDefaultTimezone: string;
}

export interface OfficeHour {
    days: string[];
    timeFra: any;
    timeTil: any;
}

export interface TransloadingFacilitySetting {
    fMaxWeight: any;
    bPalletized: boolean;
}

export enum FormFieldOptions {
    HID = 'HID',
    OPT = 'OPT',
    REQ = 'REQ'
}

export interface CupStyle {
    sButtonInfoColor: string;
    sButtonLinkColor: string;
    sButtonTextColor: string;
    sButtonErrorColor: string;
    sButtonSuccessColor: string;
    sGeneralPrimaryColor: string;
    sButtonBackgroundColor: string;
    sNavTopBackgroundColor: string;
    sNavSideBackgroundColor: string;
    sNavSideExpandControlColor: string;
    sBackgroundImageUrl: string;
    sLogoHorizontalUrl: string;
    sLogoSquareUrl: string;
}

export interface IncoTerm {
    sIncoTermCode: string;
    sIncoTermFull: string;
    sIncoTermName: string;
}

/**
 * Allow the CUP user to see documents with extra AOR Types than the default one ( External
 * visible by customer )
 */
export enum SAllowSelectDocumentsForExtraAorTypes {
    EXT = 'EXT',
    EXTCarr = 'EXT_CARR',
    EXTCarrSourceCup = 'EXT_CARR_SOURCE_CUP',
}

/**
 * Allow the CUP user to upload documents with extra AOR Types than the default one (
 * External visible by customer )
 */
export enum SAllowUploadDocumentsForExtraAorTypes {
    EXTCarr = 'EXT_CARR',
}

